import { useEffect, useRef, useState } from "react";
import { api, apiUpload } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import { RichTextEditor } from "@/components/RichTextEditor";
import { Newspaper, Plus, Pencil, Trash2, X, Copy, ExternalLink, Upload } from "lucide-react";

const STATUT_LABELS = { BROUILLON: "Brouillon", PUBLIE: "Publié" };
const STATUT_CLS = {
  BROUILLON: "bg-cfhtb-warning/10 text-cfhtb-warning border-cfhtb-warning/30",
  PUBLIE: "bg-cfhtb-success/10 text-cfhtb-success border-cfhtb-success/30",
};
const inputCls = "w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cfhtb-light";
const labelCls = "block text-sm font-medium text-cfhtb-heading mb-1.5";
const EMPTY = { titre: "", extrait: "", contenu: "", image: "", statut: "BROUILLON" };

export default function ActualitesAdmin() {
  const [articles, setArticles] = useState(null);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [imgBusy, setImgBusy] = useState(false);
  const newsImgRef = useRef(null);

  const uploadImage = async (fileList) => {
    const file = fileList?.[0];
    if (!file) return;
    setImgBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { url } = await apiUpload("/admin/actualites/image", fd);
      const abs = url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
      setEditing((e) => (e ? { ...e, image: abs } : e));
      toast.success("Image téléversée");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setImgBusy(false);
    }
  };

  const [wpArticles, setWpArticles] = useState(null);

  const load = () => api("/admin/actualites").then(setArticles).catch((e) => toast.error(e.message));
  useEffect(() => {
    load();
    api("/admin/actualites/wp").then(setWpArticles).catch(() => setWpArticles([]));
  }, []);

  const openForm = (article) => {
    setEditing(article
      ? { id: article.id, titre: article.titre, extrait: article.extrait || "", contenu: article.contenu || "", image: article.image || "", statut: article.statut }
      : { ...EMPTY });
  };

  const htmlToText = (h) => {
    const txt = DOMPurify.sanitize(h || "", { ALLOWED_TAGS: [] });
    return new DOMParser().parseFromString(txt, "text/html").documentElement.textContent;
  };

  const copierWp = (p) => {
    const extrait = htmlToText(p.extrait).trim();
    setEditing({
      titre: htmlToText(p.titre).trim(),
      extrait,
      contenu: `${p.extrait || ""}<p><a href="${p.lien}">Lire l'article complet sur cfhtb.org</a></p>`,
      image: p.image || "", statut: "BROUILLON",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = {
      titre: editing.titre, extrait: editing.extrait || null,
      contenu: editing.contenu || "", image: editing.image || null, statut: editing.statut,
    };
    try {
      if (editing.id) {
        await api(`/admin/actualites/${editing.id}`, { method: "PUT", body });
        toast.success("Actualité mise à jour");
      } else {
        await api("/admin/actualites", { method: "POST", body });
        toast.success("Actualité créée");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Supprimer « ${a.titre} » ?`)) return;
    try {
      await api(`/admin/actualites/${a.id}`, { method: "DELETE" });
      toast.success("Actualité supprimée");
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <AppLayout variant="admin">
      <div data-testid="admin-news-page" className="space-y-6 animate-fade-in">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Newspaper className="h-7 w-7 text-cfhtb-primary" />
            <div>
              <h1 className="font-serif text-3xl font-bold text-cfhtb-heading tracking-tight">Actualités</h1>
              <p className="text-sm text-cfhtb-text">
                Annonces propres à l'extranet. Les articles WordPress de cfhtb.org sont listés plus bas — copiez-en un pour le reprendre en annonce locale.
              </p>
            </div>
          </div>
          <button data-testid="news-new-button" onClick={() => openForm(null)}
            className="inline-flex items-center gap-2 bg-cfhtb-primary text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-[#1f1975] transition-colors">
            <Plus className="h-4 w-4" /> Nouvelle actualité
          </button>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
          {!articles ? (
            <p className="px-4 py-10 text-center text-sm text-cfhtb-text">Chargement…</p>
          ) : articles.length === 0 ? (
            <p className="px-4 py-10 text-center text-sm text-cfhtb-text" data-testid="news-admin-empty">
              Aucune actualité locale. Cliquez sur « Nouvelle actualité ».
            </p>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="news-admin-list">
              {articles.map((a) => (
                <li key={a.id} className="px-4 py-3 flex items-center justify-between gap-3 hover:bg-cfhtb-bg/40 transition-colors" data-testid={`news-row-${a.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    {a.image && <img src={a.image} alt="" className="h-10 w-14 object-cover rounded shrink-0" />}
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-cfhtb-heading truncate">{a.titre}</p>
                      <p className="text-xs text-cfhtb-text">
                        {a.publie_le ? `Publié le ${new Date(a.publie_le).toLocaleDateString("fr-FR")}` : "Non publié"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium border ${STATUT_CLS[a.statut]}`}>
                      {STATUT_LABELS[a.statut]}
                    </span>
                    <button data-testid={`news-edit-${a.id}`} onClick={() => openForm(a)}
                      className="p-1.5 rounded-md text-cfhtb-text hover:bg-cfhtb-bg hover:text-cfhtb-primary transition-colors">
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button data-testid={`news-delete-${a.id}`} onClick={() => remove(a)}
                      className="p-1.5 rounded-md text-cfhtb-text hover:bg-cfhtb-error/10 hover:text-cfhtb-error transition-colors">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden" data-testid="wp-news-section">
          <div className="px-5 py-3 border-b border-slate-100 bg-cfhtb-bg/40 flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-serif text-lg font-semibold text-cfhtb-heading">Articles du site cfhtb.org (WordPress)</h2>
            <span className="text-xs text-cfhtb-text">Synchronisés automatiquement — visibles par les membres</span>
          </div>
          {!wpArticles ? (
            <p className="px-4 py-8 text-center text-sm text-cfhtb-text">Chargement…</p>
          ) : wpArticles.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-cfhtb-text" data-testid="wp-news-empty">Aucun article WordPress récupéré.</p>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="wp-news-list">
              {wpArticles.map((p) => (
                <li key={p.id} className="px-4 py-3 flex items-center justify-between gap-3 hover:bg-cfhtb-bg/40 transition-colors" data-testid={`wp-news-row-${p.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    {p.image && <img src={p.image} alt="" className="h-10 w-14 object-cover rounded shrink-0" />}
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-cfhtb-heading truncate">{htmlToText(p.titre)}</p>
                      <p className="text-xs text-cfhtb-text">{p.date ? new Date(p.date).toLocaleDateString("fr-FR") : ""}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs px-2.5 py-0.5 rounded-full font-medium border bg-cfhtb-secondary/10 text-cfhtb-secondary border-cfhtb-secondary/30">WordPress</span>
                    <a href={p.lien} target="_blank" rel="noreferrer" data-testid={`wp-news-view-${p.id}`}
                      className="p-1.5 rounded-md text-cfhtb-text hover:bg-cfhtb-bg hover:text-cfhtb-primary transition-colors">
                      <ExternalLink className="h-4 w-4" />
                    </a>
                    <button data-testid={`wp-news-copy-${p.id}`} onClick={() => copierWp(p)}
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-cfhtb-primary border border-cfhtb-primary/40 rounded-md px-2.5 py-1.5 hover:bg-cfhtb-primary/5 transition-colors">
                      <Copy className="h-3.5 w-3.5" /> Copier en annonce
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" data-testid="news-form-modal">
          <form onSubmit={save} className="bg-white rounded-lg shadow-lg p-6 w-full max-w-2xl space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-xl font-semibold text-cfhtb-heading">
                {editing.id ? "Modifier l'actualité" : "Nouvelle actualité"}
              </h2>
              <button type="button" onClick={() => setEditing(null)} data-testid="news-form-close" className="text-cfhtb-text hover:text-cfhtb-heading">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div>
              <label className={labelCls}>Titre *</label>
              <input required data-testid="news-titre-input" className={inputCls} value={editing.titre}
                onChange={(e) => setEditing({ ...editing, titre: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>Extrait (résumé affiché dans les listes)</label>
              <textarea data-testid="news-extrait-input" rows={2} className={inputCls} value={editing.extrait}
                onChange={(e) => setEditing({ ...editing, extrait: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>Contenu</label>
              <RichTextEditor key={editing.id || "new"} testid="news-wysiwyg" content={editing.contenu || ""}
                onUpdate={(html) => setEditing((e) => (e ? { ...e, contenu: html } : e))} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Image</label>
                <div data-testid="news-image-dropzone"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); uploadImage(e.dataTransfer.files); }}
                  onClick={() => newsImgRef.current?.click()}
                  className="cursor-pointer rounded-md border-2 border-dashed border-cfhtb-light/60 bg-cfhtb-bg/40 hover:bg-cfhtb-bg transition-colors p-4 text-center">
                  {editing.image ? (
                    <img src={editing.image} alt="" className="mx-auto max-h-24 object-contain rounded" data-testid="news-image-preview" />
                  ) : (
                    <>
                      <Upload className="h-6 w-6 mx-auto text-cfhtb-secondary" />
                      <p className="text-xs text-cfhtb-text mt-1">{imgBusy ? "Téléversement…" : "Glissez une image ou cliquez (PNG, JPG, WebP — 5 Mo max)"}</p>
                    </>
                  )}
                  <input ref={newsImgRef} type="file" accept="image/*" className="hidden" data-testid="news-image-file"
                    onChange={(e) => { uploadImage(e.target.files); e.target.value = ""; }} />
                </div>
                <input data-testid="news-image-input" className={`${inputCls} mt-2`} value={editing.image}
                  onChange={(e) => setEditing({ ...editing, image: e.target.value })} placeholder="ou coller une URL https://…" />
              </div>
              <div>
                <label className={labelCls}>Statut</label>
                <select data-testid="news-statut-select" className={inputCls} value={editing.statut}
                  onChange={(e) => setEditing({ ...editing, statut: e.target.value })}>
                  <option value="BROUILLON">Brouillon</option>
                  <option value="PUBLIE">Publié (visible par les membres)</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setEditing(null)}
                className="bg-white text-cfhtb-text border border-slate-300 rounded-md px-4 py-2 text-sm font-medium hover:bg-slate-50">
                Annuler
              </button>
              <button type="submit" data-testid="news-save-button" disabled={busy}
                className="bg-cfhtb-primary text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-[#1f1975] disabled:opacity-60 transition-colors">
                {busy ? "Enregistrement…" : "Enregistrer"}
              </button>
            </div>
          </form>
        </div>
      )}
    </AppLayout>
  );
}
